/**
 * Boot URL parameters (architecture §8.3, GDD §4.15, ui-ux §22.1) → one typed `BootParams`. Pure (no DOM): the app
 * passes `new URLSearchParams(location.search)`. Aliases are accepted and listed in `aliases` (the app logs them once);
 * bad values fall back to defaults with a message in `warnings`.
 *
 * Feature params (combat `fight, auto, …`, exploration `region, …`, creatures `dna, clip, …`, …) are NOT parsed here:
 * screens read them through `ScreenDef.parseParams`.
 */
import { FIXTURE_IDS, type FixtureId, type QualityTier } from '../shared';

export type OverlayId = 'combat' | 'perf' | 'ui' | 'anim';
export type MotionLevel = 'full' | 'reduced' | 'min';
export type SimulateKind = 'no-adapter' | 'device-failed' | 'insecure' | 'device-lost' | 'save-corrupt' | 'storage-full' | 'slow-gpu'
  | 'low-fps' | 'second-tab' | 'app-failed';

export interface BootParams {
  /** `screen` / `view`: ScreenId or alias (converted to its hash route by the router). */
  screen: string | null;
  /** `?fight=demo` compatibility → `#/arena/fight/demo` (ui-ux §3.3). */
  fight: string | null;
  debug: boolean;
  overlays: OverlayId[];
  /** Session seed (in-memory save). */
  seed: string | null;
  /** Storage mode: persistent (the player's save), memory (copy of the current save) or fresh (new in-memory save). */
  save: 'persistent' | 'memory' | 'fresh';
  /** `fresh=1` = `save=fresh` + FTUE. */
  ftue: boolean;
  fixture: FixtureId | null;
  /** `fixture=<x>` that is not a save fixture (creatures' playlog alias inside the Animation Lab). */
  playlog: string | null;
  paused: boolean;
  /** Presentation ms to step to after the first screen enters (with `paused=1`). */
  t: number | null;
  timeScale: number | null;
  cadence: number | null;
  jitter: number;
  /** GDD §10.0 slice rules (default on). */
  slice: boolean;
  power: 'auto' | 'max';
  /** `ui=0`: hide the HTML overlay. */
  hideUi: boolean;
  /** `lite=1` / `render=none`: «Без миниатюр». */
  lite: boolean;
  /** `nowebgpu=1`: simulate E1 (→ lite + banner). */
  nowebgpu: boolean;
  simulate: SimulateKind | null;
  quality: QualityTier | null;
  renderScale: number | null;
  msaa: 1 | 4 | null;
  motion: MotionLevel | null;
  /** `wall=<ISO>` (alias `date`), epoch ms. */
  wall: number | null;
  /** `away=<duration>` (alias `offline`), ms. */
  away: number | null;
  level: number | null;
  textScale: 100 | 115 | 130 | 150 | null;
  leftHanded: boolean | null;
  /** `safe=T,R,B,L` safe-area override in CSS px. */
  safe: { t: number; r: number; b: number; l: number } | null;
  font: 'plain' | 'fallback' | null;
  theme: 'day' | 'night' | null;
  display: 'browser' | 'standalone' | null;
  /** `title=1`: show the title even with a save. */
  title: boolean;
  /** Aliases that were used (`'view→screen'`), for a one-time console note. */
  aliases: string[];
  warnings: string[];
}

const QUALITY: Record<string, QualityTier> = { low: 'low', med: 'med', mid: 'med', medium: 'med', high: 'high', ultra: 'ultra' };
const SIMULATE: readonly SimulateKind[] = ['no-adapter', 'device-failed', 'insecure', 'device-lost', 'save-corrupt', 'storage-full',
  'slow-gpu', 'low-fps', 'second-tab', 'app-failed'];

/** True for a GDD §4.15 save-fixture id. */
export const isFixtureId = (x: string): x is FixtureId => (FIXTURE_IDS as readonly string[]).includes(x);

/**
 * `8h`, `30m`, `2d`, `90s`, `1h30m`, `1500ms`, or a bare number (= hours, the GDD's `away=8h` unit). Returns ms or null.
 */
export function parseDuration(s: string): number | null {
  const str = s.trim().toLowerCase();
  if (str === '') return null;
  if (/^\d+(\.\d+)?$/.test(str)) return Number(str) * 3_600_000;
  const unit: Record<string, number> = { ms: 1, s: 1000, m: 60_000, min: 60_000, h: 3_600_000, d: 86_400_000, w: 604_800_000 };
  let total = 0;
  let rest = str;
  const re = /^(\d+(?:\.\d+)?)(ms|min|s|m|h|d|w)/;
  while (rest.length > 0) {
    const m = re.exec(rest);
    if (!m) return null;
    total += Number(m[1]) * unit[m[2]];
    rest = rest.slice(m[0].length);
  }
  return total;
}

/** ISO date-time (or epoch ms) → epoch ms; null when unparsable. */
export function parseWall(s: string): number | null {
  if (/^\d{10,}$/.test(s)) return Number(s);
  const v = Date.parse(s);
  return Number.isFinite(v) ? v : null;
}

/** Parses every boot parameter. */
export function parseBootParams(q: URLSearchParams): BootParams {
  const warnings: string[] = [];
  const aliases: string[] = [];
  const get = (name: string, ...alts: string[]): string | null => {
    const v = q.get(name);
    if (v !== null) return v;
    for (const a of alts) {
      const w = q.get(a);
      if (w !== null) { aliases.push(`${a}→${name}`); return w; }
    }
    return null;
  };
  const flag = (v: string | null): boolean => v !== null && v !== '0' && v !== 'false' && v !== 'off';
  const num = (name: string, v: string | null, lo: number, hi: number): number | null => {
    if (v === null || v === '') return null;
    const n = Number(v);
    if (!Number.isFinite(n) || n < lo || n > hi) { warnings.push(`${name}=${v} ignored (expected ${lo}..${hi})`); return null; }
    return n;
  };

  // overlay (+ fps=1 → perf, debug=combat → combat); fps=<hz ≥ 24> → cadence (creatures' alias)
  const overlays = new Set<OverlayId>();
  for (const o of (q.get('overlay') ?? '').split(',').map((x) => x.trim()).filter(Boolean)) {
    if (o === 'combat' || o === 'perf' || o === 'ui' || o === 'anim') overlays.add(o);
    else warnings.push(`overlay=${o} ignored`);
  }
  const fps = q.get('fps');
  let cadence = num('cadence', q.get('cadence'), 1, 1000);
  if (fps !== null) {
    const n = Number(fps);
    if (n === 1) { overlays.add('perf'); aliases.push('fps=1→overlay=perf'); }
    else if (Number.isFinite(n) && n >= 24 && cadence === null) { cadence = n; aliases.push('fps→cadence'); }
  }
  const debugRaw = q.get('debug');
  if (debugRaw === 'combat') { overlays.add('combat'); aliases.push('debug=combat→overlay=combat'); }

  // storage mode
  let save: BootParams['save'] = 'persistent';
  const saveRaw = q.get('save');
  if (saveRaw === 'memory' || saveRaw === 'fresh') save = saveRaw;
  else if (saveRaw !== null) warnings.push(`save=${saveRaw} ignored`);
  const ftue = flag(q.get('fresh'));
  if (ftue) save = 'fresh';

  // fixtures (+ creatures' fixture=<playlog> alias)
  let fixture: FixtureId | null = null;
  let playlog = q.get('playlog');
  const fx = q.get('fixture');
  if (fx !== null) {
    if (isFixtureId(fx)) fixture = fx;
    else if (playlog === null) { playlog = fx; aliases.push('fixture→playlog'); }
  }

  const seed = q.get('seed');
  if (save === 'persistent' && (fixture !== null || (seed !== null && seed !== ''))) save = 'memory';

  // clock
  const clockRaw = q.get('clock');
  const paused = flag(q.get('paused')) || clockRaw === 'manual';
  if (clockRaw === 'manual') aliases.push('clock=manual→paused=1');
  const t = num('t', q.get('t'), 0, 1e9);
  const timeScale = num('timeScale', q.get('timeScale'), 0.05, 8);
  const jitter = num('jitter', q.get('jitter'), 0, 0.3) ?? 0;

  // quality
  const qRaw = get('q', 'quality');
  let quality: QualityTier | null = null;
  if (qRaw !== null) {
    quality = QUALITY[qRaw.toLowerCase()] ?? null;
    if (!quality) warnings.push(`q=${qRaw} ignored`);
  }
  const msaaRaw = q.get('msaa');
  const msaa = msaaRaw === '1' ? 1 : msaaRaw === '4' ? 4 : null;

  // motion (rm=1, reducedMotion=1 → reduced)
  let motion: MotionLevel | null = null;
  const mRaw = q.get('motion');
  if (mRaw === 'full' || mRaw === 'reduced' || mRaw === 'min') motion = mRaw;
  else if (mRaw !== null) warnings.push(`motion=${mRaw} ignored`);
  if (motion === null && (flag(q.get('rm')) || flag(q.get('reducedMotion')))) { motion = 'reduced'; aliases.push('rm→motion=reduced'); }

  // lite (render=none = lite=1)
  const renderRaw = q.get('render');
  const lite = flag(q.get('lite')) || renderRaw === 'none';
  if (renderRaw === 'none') aliases.push('render=none→lite=1');

  const simRaw = q.get('simulate');
  const simulate = simRaw !== null && (SIMULATE as readonly string[]).includes(simRaw) ? (simRaw as SimulateKind) : null;
  if (simRaw !== null && simulate === null) warnings.push(`simulate=${simRaw} ignored`);

  const wallRaw = get('wall', 'date');
  const wall = wallRaw !== null ? parseWall(wallRaw) : null;
  if (wallRaw !== null && wall === null) warnings.push(`wall=${wallRaw} ignored`);
  const awayRaw = get('away', 'offline');
  const away = awayRaw !== null ? parseDuration(awayRaw) : null;
  if (awayRaw !== null && away === null) warnings.push(`away=${awayRaw} ignored`);

  const tsRaw = q.get('textscale');
  let textScale: BootParams['textScale'] = null;
  if (tsRaw !== null) {
    const n = Number(tsRaw);
    const pct = n <= 2 ? Math.round(n * 100) : Math.round(n);
    if (pct === 100 || pct === 115 || pct === 130 || pct === 150) textScale = pct;
    else warnings.push(`textscale=${tsRaw} ignored`);
  }

  let safe: BootParams['safe'] = null;
  const safeRaw = q.get('safe');
  if (safeRaw !== null) {
    const parts = safeRaw.split(',').map(Number);
    if (parts.length === 4 && parts.every((x) => Number.isFinite(x) && x >= 0)) safe = { t: parts[0], r: parts[1], b: parts[2], l: parts[3] };
    else warnings.push(`safe=${safeRaw} ignored (expected T,R,B,L)`);
  }

  const pick = <T extends string>(name: string, allowed: readonly T[]): T | null => {
    const v = q.get(name);
    if (v === null) return null;
    if ((allowed as readonly string[]).includes(v)) return v as T;
    warnings.push(`${name}=${v} ignored`);
    return null;
  };
  const levelN = num('level', q.get('level'), 1, 999);
  const scaleN = num('scale', q.get('scale'), 0.25, 1);

  return {
    screen: get('screen', 'view'),
    fight: q.get('fight'),
    debug: debugRaw !== null && debugRaw !== '0',
    overlays: [...overlays],
    seed: seed !== null && seed !== '' ? seed : null,
    save,
    ftue,
    fixture,
    playlog,
    paused,
    t,
    timeScale,
    cadence,
    jitter,
    slice: q.get('slice') !== '0',
    power: q.get('power') === 'max' ? 'max' : 'auto',
    hideUi: q.get('ui') === '0',
    lite,
    nowebgpu: flag(q.get('nowebgpu')),
    simulate,
    quality,
    renderScale: scaleN,
    msaa,
    motion,
    wall,
    away,
    level: levelN === null ? null : Math.round(levelN),
    textScale,
    leftHanded: q.get('lefthand') === null ? null : flag(q.get('lefthand')),
    safe,
    font: pick('font', ['plain', 'fallback'] as const),
    theme: pick('theme', ['day', 'night'] as const),
    display: pick('display', ['browser', 'standalone'] as const),
    title: flag(q.get('title')),
    aliases,
    warnings,
  };
}
